/**
 * Symbol library v1 — IEC 60617 shapes for Belgian residential/tertiary work.
 *
 * Conventions (enforced by tests):
 * - millimetres; width and height are multiples of 5; ports sit on the 2.5 mm grid,
 *   so ports stay on the grid in every rotation;
 * - vertical symbols: supply side at the top (port "in" / "inN"), load side at the bottom.
 * To be reviewed with electricians and inspectors (see docs/requirements/rgie.md, DOC-03).
 */
import type { FieldDef, Localised, SymbolDef, SymbolPort } from './types.ts';

// ---------- rooms ----------

/** Room types for position plans; `wet` rooms need ≤ 30 mA protection (RGIE, to verify). */
export const ROOM_TYPES: Array<{ value: string; label: Localised; wet: boolean }> = [
  { value: 'living', label: { fr: 'Séjour', en: 'Living room', nl: 'Woonkamer' }, wet: false },
  { value: 'kitchen', label: { fr: 'Cuisine', en: 'Kitchen', nl: 'Keuken' }, wet: false },
  { value: 'bedroom', label: { fr: 'Chambre', en: 'Bedroom', nl: 'Slaapkamer' }, wet: false },
  {
    value: 'bathroom',
    label: { fr: 'Salle de bain / douche', en: 'Bathroom / shower', nl: 'Badkamer / douche' },
    wet: true,
  },
  { value: 'toilet', label: { fr: 'WC', en: 'Toilet', nl: 'Toilet' }, wet: false },
  { value: 'laundry', label: { fr: 'Buanderie', en: 'Laundry', nl: 'Wasplaats' }, wet: true },
  { value: 'hall', label: { fr: 'Hall / couloir', en: 'Hall', nl: 'Hal / gang' }, wet: false },
  { value: 'office', label: { fr: 'Bureau', en: 'Office', nl: 'Bureau' }, wet: false },
  { value: 'garage', label: { fr: 'Garage', en: 'Garage', nl: 'Garage' }, wet: false },
  { value: 'cellar', label: { fr: 'Cave', en: 'Cellar', nl: 'Kelder' }, wet: false },
  { value: 'attic', label: { fr: 'Grenier', en: 'Attic', nl: 'Zolder' }, wet: false },
  { value: 'outdoor', label: { fr: 'Extérieur', en: 'Outdoor', nl: 'Buiten' }, wet: true },
  { value: 'other', label: { fr: 'Autre', en: 'Other', nl: 'Andere' }, wet: false },
];

// ---------- helpers ----------

const circle = (cx: number, cy: number, r: number) =>
  `M${cx + r} ${cy} A${r} ${r} 0 1 1 ${cx - r} ${cy} A${r} ${r} 0 1 1 ${cx + r} ${cy} Z`;
const rect = (x: number, y: number, w: number, h: number) => `M${x} ${y} H${x + w} V${y + h} H${x} Z`;
const L = (fr: string, en: string, nl: string): Localised => ({ fr, en, nl });

const inOut = (h: number, x = 5): SymbolPort[] => [
  { id: 'in', x, y: 0, dir: 'up' },
  { id: 'out', x, y: h, dir: 'down' },
];
const inOnly = (x = 5): SymbolPort[] => [{ id: 'in', x, y: 0, dir: 'up' }];

// ---------- fields ----------

const F = {
  ratingA: (d = 16): FieldDef => ({
    key: 'ratingA',
    label: L('Calibre', 'Rating', 'Nominale stroom'),
    type: 'number',
    unit: 'A',
    default: d,
    min: 0,
  }),
  curve: (): FieldDef => ({
    key: 'curve',
    label: L('Courbe', 'Curve', 'Curve'),
    type: 'select',
    options: [{ value: 'B' }, { value: 'C' }, { value: 'D' }],
    default: 'C',
  }),
  poles: (d = 2): FieldDef => ({
    key: 'poles',
    label: L('Pôles', 'Poles', 'Polen'),
    type: 'select',
    options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
    default: d,
  }),
  breakingKa: (): FieldDef => ({
    key: 'breakingKa',
    label: L('Pouvoir de coupure', 'Breaking capacity', 'Onderbrekingsvermogen'),
    type: 'number',
    unit: 'kA',
    min: 0,
  }),
  sensitivityMa: (d = 30): FieldDef => ({
    key: 'sensitivityMa',
    label: L('Sensibilité', 'Sensitivity', 'Gevoeligheid'),
    type: 'select',
    options: [{ value: 10 }, { value: 30 }, { value: 100 }, { value: 300 }, { value: 500 }],
    default: d,
  }),
  rcdType: (): FieldDef => ({
    key: 'rcdType',
    label: L('Type', 'Type', 'Type'),
    type: 'select',
    options: [{ value: 'AC' }, { value: 'A' }, { value: 'F' }, { value: 'B' }],
    default: 'A',
  }),
  powerKw: (): FieldDef => ({
    key: 'powerKw',
    label: L('Puissance', 'Power', 'Vermogen'),
    type: 'number',
    unit: 'kW',
    min: 0,
    step: 0.1,
  }),
  count: (): FieldDef => ({
    key: 'count',
    label: L('Nombre', 'Count', 'Aantal'),
    type: 'select',
    options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
    default: 1,
    omitDefault: true,
  }),
  ip: (): FieldDef => ({
    key: 'ip',
    label: L('Indice IP', 'IP rating', 'IP-graad'),
    type: 'select',
    options: [
      { value: '', label: L('—', '—', '—') },
      { value: 'IP44' },
      { value: 'IP55' },
      { value: 'IP65' },
    ],
    default: '',
  }),
  description: (): FieldDef => ({
    key: 'description',
    label: L('Description', 'Description', 'Omschrijving'),
    type: 'text',
  }),
  setting: (): FieldDef => ({
    key: 'settingA',
    label: L('Réglage', 'Setting', 'Instelling'),
    type: 'number',
    unit: 'A',
    step: 0.1,
  }),
};

const breakerFields = () => [F.ratingA(), F.curve(), F.poles(), F.breakingKa()];
const rcdFields = () => [F.ratingA(40), F.sensitivityMa(), F.rcdType(), F.poles()];

// ---------- single-pole building blocks (conductor at x = c) ----------

/** Switching blade from the moving contact (c, bottom) up-left to the open position. */
const blade = (c: number, top = 6.5, bottom = 15) => `M${c} ${bottom} L${c - 3.5} ${top}`;
const breakerPole = (c: number) => [
  `M${c} 0 V6`,
  `M${c - 1.5} 4.5 L${c + 1.5} 7.5 M${c + 1.5} 4.5 L${c - 1.5} 7.5`,
  blade(c),
  `M${c} 15 V20`,
];
const switchPole = (c: number) => [`M${c} 0 V6`, blade(c), `M${c} 15 V20`];
const disconnectorPole = (c: number) => [...switchPole(c), `M${c - 1.5} 6 H${c + 1.5}`];
const loadSwitchPole = (c: number) => [`M${c} 0 V5`, circle(c, 5.75, 0.75), blade(c), `M${c} 15 V20`];
const fusePole = (c: number) => [`M${c} 0 V20`, rect(c - 2, 5, 4, 10)];
const contactorPole = (c: number) => [
  `M${c} 0 V4`,
  `M${c} 4 A1.25 1.25 0 0 1 ${c} 6.5`,
  blade(c, 5.5, 12.5),
  `M${c} 12.5 V15`,
];
const overloadPole = (c: number) => [`M${c} 0 V4`, `M${c} 4 V6.5 H${c + 1.5} V10.5 H${c} V15`];

/** Residual-current sensing box ("I" + Δ drawn as a triangle, so no special glyph is needed). */
const rcdBox = (x: number) => ({
  paths: [rect(x, 8.5, 3.5, 4.5), `M${x + 2} 11.9 L${x + 2.7} 10 L${x + 3.3} 11.9 Z`],
  texts: [{ text: 'I', x: x + 1, y: 12, size: 2.2 }],
});

/** Build an n-pole device from a single-pole drawing; poles 10 mm apart, dashed mechanical link. */
function multipole(
  base: Omit<SymbolDef, 'paths' | 'ports' | 'width' | 'height' | 'dashed'>,
  n: number,
  pole: (c: number) => string[],
  height: number,
  linkY: number | null,
): SymbolDef {
  const paths: string[] = [];
  const ports: SymbolPort[] = [];
  for (let i = 0; i < n; i++) {
    const c = 5 + i * 10;
    paths.push(...pole(c));
    ports.push(
      { id: `in${i + 1}`, x: c, y: 0, dir: 'up' },
      { id: `out${i + 1}`, x: c, y: height, dir: 'down' },
    );
  }
  return {
    ...base,
    width: n * 10,
    height,
    paths,
    ports,
    dashed: linkY === null ? [] : [`M${5 - 2} ${linkY} H${5 + (n - 1) * 10 - 2}`],
  };
}

// ---------- appliance box ----------

function appliance(
  id: string,
  name: Localised,
  marks: string[],
  fills: string[] = [],
  keywords: string[] = [],
  texts: NonNullable<SymbolDef['texts']> = [],
): SymbolDef {
  return {
    id,
    name,
    category: 'appliance',
    views: ['single-line', 'position-plan'],
    tagPrefix: 'E',
    keywords,
    width: 10,
    height: 10,
    paths: ['M5 0 V2', rect(1, 2, 8, 8), ...marks],
    fills,
    texts,
    ports: inOnly(),
    fields: [F.powerKw(), F.description()],
    label: ['{powerKw} kW', '{description}'],
  };
}

// ---------- installation switch (plan) helpers: circle at (5,5), r = 1.5 ----------

const swCircle = circle(5, 5, 1.5);
const tick = (x: number, y: number, dx: number, dy: number) => `M${x} ${y} L${x + dx} ${y + dy}`;
const swPort: SymbolPort[] = [{ id: 'in', x: 5, y: 7.5, dir: 'down' }];

// ---------- control contacts (conductor x = 5, height 15) ----------

const noContact = ['M5 0 V5', 'M5 10 L1.5 4', 'M5 10 V15'];
const ncContact = ['M5 0 V5 H8', 'M5 10 L8.5 4.5', 'M5 10 V15'];
const actuator = (y = 7) => `M3.25 ${y} H-2.5`;

const X_CROSS = 'M1.46 6.46 L8.54 13.54 M8.54 6.46 L1.46 13.54';

// ---------- the library ----------

const S: SymbolDef[] = [
  // ===== Protection & distribution (single pole drawing; pole count is a property) =====
  {
    id: 'iec.supply',
    name: L('Arrivée réseau', 'Mains supply', 'Netaansluiting'),
    category: 'distribution',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 10,
    paths: ['M5 0 V10'],
    fills: ['M5 10 L3 6 H7 Z'],
    ports: [{ id: 'out', x: 5, y: 10, dir: 'down' }],
    tagPrefix: 'W',
    fields: [
      {
        key: 'voltage',
        label: L('Tension', 'Voltage', 'Spanning'),
        type: 'select',
        options: [{ value: '230 V' }, { value: '3×230 V' }, { value: '3N×400 V' }],
        default: '230 V',
      },
      {
        key: 'ratingA',
        label: L('Puissance de raccordement', 'Connection rating', 'Aansluitvermogen'),
        type: 'number',
        unit: 'A',
        default: 40,
      },
    ],
    label: ['{voltage}', '{ratingA} A'],
    keywords: ['réseau', 'grid', 'branchement', 'GRD'],
  },
  {
    id: 'iec.meter',
    name: L('Compteur', 'Energy meter', 'Meter'),
    category: 'distribution',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 20,
    paths: ['M5 0 V4', rect(1, 4, 8, 12), 'M5 16 V20'],
    texts: [{ text: 'kWh', x: 5, y: 11, size: 2.3 }],
    ports: inOut(20),
    tagPrefix: 'P',
    keywords: ['kwh', 'compteur', 'meter', 'teller'],
  },
  {
    id: 'iec.breaker',
    name: L('Disjoncteur', 'Circuit breaker', 'Automaat'),
    category: 'protection',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 20,
    paths: breakerPole(5),
    ports: inOut(20),
    tagPrefix: 'Q',
    fields: breakerFields(),
    label: ['{curve}{ratingA} A'],
    keywords: ['MCB', 'disjoncteur', 'automatique', 'automaat'],
  },
  {
    id: 'iec.rcd',
    name: L('Interrupteur différentiel', 'Residual current device', 'Differentieelschakelaar'),
    category: 'protection',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 20,
    paths: [...switchPole(5), ...rcdBox(6.5).paths],
    dashed: ['M3.4 10.75 H6.5'],
    texts: rcdBox(6.5).texts,
    ports: inOut(20),
    tagPrefix: 'Q',
    fields: rcdFields(),
    label: ['{ratingA} A', '{sensitivityMa} mA', 'type {rcdType}'],
    keywords: ['RCD', 'différentiel', 'ID', 'RCCB', 'aardlek', 'verliesstroomschakelaar'],
  },
  {
    id: 'iec.rcbo',
    name: L('Disjoncteur différentiel', 'RCBO', 'Differentieelautomaat'),
    category: 'protection',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 20,
    paths: [...breakerPole(5), ...rcdBox(6.5).paths],
    dashed: ['M3.4 10.75 H6.5'],
    texts: rcdBox(6.5).texts,
    ports: inOut(20),
    tagPrefix: 'Q',
    fields: [F.ratingA(), F.curve(), F.sensitivityMa(), F.rcdType(), F.poles()],
    label: ['{curve}{ratingA} A', '{sensitivityMa} mA', 'type {rcdType}'],
    keywords: ['RCBO', 'DD', 'différentiel'],
  },
  {
    id: 'iec.fuse',
    name: L('Fusible', 'Fuse', 'Smeltveiligheid'),
    category: 'protection',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 20,
    paths: fusePole(5),
    ports: inOut(20),
    tagPrefix: 'F',
    fields: [F.ratingA()],
    label: ['{ratingA} A'],
    keywords: ['fusible', 'fuse', 'smeltveiligheid', 'zekering'],
  },
  {
    id: 'iec.switch-disconnector',
    name: L('Interrupteur-sectionneur', 'Switch-disconnector', 'Lastscheider'),
    category: 'protection',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 20,
    paths: disconnectorPole(5),
    ports: inOut(20),
    tagPrefix: 'Q',
    fields: [F.ratingA(40), F.poles()],
    label: ['{ratingA} A'],
    keywords: ['sectionneur', 'interrupteur général', 'main switch', 'hoofdschakelaar'],
  },
  {
    id: 'iec.load-switch',
    name: L('Interrupteur de puissance', 'Load switch', 'Lastschakelaar'),
    category: 'protection',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 20,
    paths: loadSwitchPole(5),
    ports: inOut(20),
    tagPrefix: 'Q',
    fields: [F.ratingA(20), F.poles()],
    label: ['{ratingA} A'],
  },
  {
    id: 'iec.spd',
    name: L('Parafoudre', 'Surge protective device', 'Overspanningsbeveiliging'),
    category: 'protection',
    views: ['single-line', 'multi-line'],
    width: 10,
    height: 20,
    paths: ['M5 0 V4', rect(2.5, 4, 5, 10), 'M5 5.5 V11', 'M5 14 V20'],
    fills: ['M5 12.5 L3.9 10.3 H6.1 Z'],
    ports: [
      { id: 'in', x: 5, y: 0, dir: 'up' },
      { id: 'pe', x: 5, y: 20, dir: 'down' },
    ],
    tagPrefix: 'F',
    fields: [
      {
        key: 'spdType',
        label: L('Type', 'Type', 'Type'),
        type: 'select',
        options: [{ value: 'T1' }, { value: 'T2' }, { value: 'T1+T2' }, { value: 'T3' }],
        default: 'T2',
      },
    ],
    label: ['{spdType}'],
    keywords: ['SPD', 'surtension', 'overspanning', 'bliksem'],
  },
  {
    id: 'iec.contactor',
    name: L('Contacteur (pôle de puissance)', 'Contactor (main contact)', 'Contactor (vermogenscontact)'),
    category: 'distribution',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 15,
    paths: contactorPole(5),
    ports: inOut(15),
    tagPrefix: 'K',
    fields: [F.ratingA(25), F.poles()],
    label: ['{ratingA} A'],
    keywords: ['contacteur', 'relais', 'magneetschakelaar', 'jour/nuit'],
  },
  {
    id: 'iec.overload',
    name: L('Relais thermique', 'Thermal overload relay', 'Thermisch relais'),
    category: 'protection',
    views: ['multi-line', 'control'],
    width: 10,
    height: 15,
    paths: [...overloadPole(5), rect(2, 3, 6, 9)],
    ports: inOut(15),
    tagPrefix: 'F',
    fields: [F.setting()],
    label: ['{settingA} A'],
    keywords: ['thermique', 'thermisch relais'],
  },
  {
    id: 'iec.transformer',
    name: L('Transformateur', 'Transformer', 'Transformator'),
    category: 'distribution',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 20,
    paths: ['M5 0 V2.5', circle(5, 6.5, 4), circle(5, 13.5, 4), 'M5 17.5 V20'],
    ports: inOut(20),
    tagPrefix: 'T',
    fields: [
      { key: 'primaryV', label: L('Primaire', 'Primary', 'Primair'), type: 'text', default: '230 V' },
      { key: 'secondaryV', label: L('Secondaire', 'Secondary', 'Secundair'), type: 'text', default: '12 V' },
    ],
    label: ['{primaryV} / {secondaryV}'],
    keywords: ['transfo', 'sonnerie', 'bell transformer', 'beltransformator'],
  },
  {
    id: 'iec.time-switch',
    name: L('Horloge / minuterie', 'Time switch', 'Schakelklok / tijdschakelaar'),
    category: 'distribution',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 20,
    paths: ['M5 0 V4', rect(1, 4, 8, 12), circle(5, 10, 2.75), 'M5 10 V8', 'M5 10 H6.5', 'M5 16 V20'],
    ports: inOut(20),
    tagPrefix: 'K',
    keywords: ['minuterie', 'horloge', 'timer', 'schakelklok', 'trappenhuisautomaat'],
  },

  // ===== Outlets =====
  {
    id: 'iec.socket',
    name: L('Prise de courant avec terre', 'Socket outlet with earth', 'Stopcontact met aarding'),
    category: 'outlet',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V4', 'M1 9 A4 4 0 0 1 9 9', 'M1 4 H9'],
    ports: inOnly(),
    tagPrefix: 'X',
    fields: [F.count(), F.ip()],
    label: ['{count}×', '{ip}'],
    keywords: ['prise', 'socket', 'stopcontact'],
  },
  {
    id: 'iec.socket-noearth',
    name: L('Prise de courant sans terre', 'Socket outlet without earth', 'Stopcontact zonder aarding'),
    category: 'outlet',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V5', 'M1 9 A4 4 0 0 1 9 9'],
    ports: inOnly(),
    tagPrefix: 'X',
    fields: [F.count()],
    label: ['{count}×'],
    keywords: ['prise', 'stopcontact'],
  },
  {
    id: 'iec.socket-3p',
    name: L('Prise triphasée', 'Three-phase socket outlet', 'Driefasig stopcontact'),
    category: 'outlet',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V4', 'M1 9 A4 4 0 0 1 9 9', 'M1 4 H9', 'M3.5 3 L5.5 1 M4.5 3 L6.5 1 M5.5 3 L7.5 1'],
    ports: inOnly(),
    tagPrefix: 'X',
    fields: [F.ratingA(16)],
    label: ['{ratingA} A'],
    keywords: ['triphasé', 'CEE', 'perilex', 'driefasig'],
  },
  {
    id: 'iec.socket-data',
    name: L('Prise télécom / données', 'Telecom / data outlet', 'Telecom- / datacontactdoos'),
    category: 'outlet',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V5', 'M1 9 A4 4 0 0 1 9 9 Z'],
    texts: [{ text: 'TP', x: 5, y: 8.4, size: 2.2 }],
    ports: inOnly(),
    tagPrefix: 'X',
    keywords: ['RJ45', 'réseau', 'ethernet', 'téléphone', 'data', 'UTP'],
  },
  {
    id: 'iec.socket-tv',
    name: L('Prise TV', 'TV outlet', 'TV-aansluiting'),
    category: 'outlet',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V5', 'M1 9 A4 4 0 0 1 9 9 Z'],
    texts: [{ text: 'TV', x: 5, y: 8.4, size: 2.2 }],
    ports: inOnly(),
    tagPrefix: 'X',
    keywords: ['coax', 'antenne', 'télévision'],
  },

  // ===== Fixed appliances =====
  appliance(
    'iec.cooker',
    L('Cuisinière / taque', 'Cooker / hob', 'Fornuis / kookplaat'),
    [],
    [circle(3.5, 4.5, 0.8), circle(6.5, 4.5, 0.8), circle(3.5, 7.5, 0.8), circle(6.5, 7.5, 0.8)],
    ['taque', 'induction', 'kookplaat', 'fornuis'],
  ),
  appliance(
    'iec.oven',
    L('Four', 'Oven', 'Oven'),
    [rect(2.5, 5, 5, 3.5), 'M2.5 3.75 H7.5'],
    [],
    ['four', 'micro-ondes', 'oven'],
  ),
  appliance(
    'iec.washing-machine',
    L('Lave-linge', 'Washing machine', 'Wasmachine'),
    [circle(5, 6, 2.5)],
    [],
    ['machine à laver', 'wasmachine'],
  ),
  appliance(
    'iec.dryer',
    L('Sèche-linge', 'Tumble dryer', 'Droogkast'),
    [circle(5, 6, 2.5)],
    [circle(5, 6, 0.7)],
    ['séchoir', 'droogkast'],
  ),
  appliance(
    'iec.dishwasher',
    L('Lave-vaisselle', 'Dishwasher', 'Vaatwasser'),
    ['M2.5 4.5 H7.5 M2.5 6.5 H7.5 M2.5 8.5 H7.5'],
    [],
    ['vaatwas', 'vaatwasmachine'],
  ),
  appliance(
    'iec.water-heater',
    L('Chauffe-eau', 'Water heater', 'Boiler'),
    ['M3 8.5 L4 4 L5 8.5 L6 4 L7 8.5'],
    [],
    ['boiler', 'chauffe-eau', 'ballon'],
  ),
  appliance(
    'iec.heater',
    L('Radiateur électrique', 'Electric heater', 'Elektrische verwarming'),
    ['M3 3.5 V8.5 M5 3.5 V8.5 M7 3.5 V8.5'],
    [],
    ['convecteur', 'accumulateur', 'verwarming'],
  ),
  appliance(
    'iec.heat-pump',
    L('Pompe à chaleur', 'Heat pump', 'Warmtepomp'),
    [circle(5, 6, 2.75), 'M5 6 L5 3.5 M5 6 L7.2 7.3 M5 6 L2.8 7.3'],
    [],
    ['PAC', 'warmtepomp', 'airco', 'climatisation'],
  ),
  appliance(
    'iec.ev-charger',
    L('Borne de recharge VE', 'EV charger', 'Laadpaal'),
    [],
    ['M5.6 3 L3.4 6.4 H5 L4.4 9 L6.6 5.6 H5 Z'],
    ['voiture électrique', 'laadpaal', 'wallbox', 'laadpunt'],
  ),
  appliance(
    'iec.inverter',
    L('Onduleur PV', 'PV inverter', 'PV-omvormer'),
    ['M1 10 L9 2'],
    [],
    ['photovoltaïque', 'omvormer', 'solaire', 'onduleur'],
    [
      { text: '=', x: 3.2, y: 5.2, size: 2.6 },
      { text: '~', x: 6.8, y: 8.8, size: 2.6 },
    ],
  ),
  {
    id: 'iec.pv-array',
    name: L('Panneaux photovoltaïques', 'PV array', 'Zonnepanelen'),
    category: 'appliance',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V2', rect(1, 2, 8, 8), 'M1 4.5 H9 M1 7.5 H9 M3.5 2 V10 M6.5 2 V10'],
    ports: inOnly(),
    tagPrefix: 'G',
    fields: [
      {
        key: 'powerKwp',
        label: L('Puissance crête', 'Peak power', 'Piekvermogen'),
        type: 'number',
        unit: 'kWc',
        step: 0.1,
      },
    ],
    label: ['{powerKwp} kWc'],
    keywords: ['solaire', 'zonnepanelen', 'PV'],
  },
  appliance(
    'iec.ventilation',
    L('Ventilation / VMC', 'Ventilation fan', 'Ventilatie'),
    [circle(5, 6, 2.75), 'M5 6 Q6.5 4 5 3.25 M5 6 Q3 7 2.75 5.5 M5 6 Q5.5 8.25 7 7.25'],
    [],
    ['VMC', 'extracteur', 'ventilatie', 'hotte'],
  ),
  appliance(
    'iec.appliance',
    L('Appareil fixe', 'Fixed appliance', 'Vast toestel'),
    [],
    [],
    ['appareil', 'toestel', 'récepteur'],
  ),

  // ===== Lighting =====
  {
    id: 'iec.light',
    name: L('Point lumineux', 'Lighting point', 'Lichtpunt'),
    category: 'lighting',
    views: ['single-line', 'multi-line', 'position-plan'],
    width: 10,
    height: 15,
    paths: ['M5 0 V5', circle(5, 10, 5), X_CROSS],
    ports: inOnly(),
    tagPrefix: 'E',
    fields: [F.count()],
    label: ['{count}×'],
    keywords: ['lampe', 'éclairage', 'lichtpunt', 'luminaire', 'plafonnier'],
  },
  {
    id: 'iec.light-wall',
    name: L('Applique murale', 'Wall light', 'Wandlamp'),
    category: 'lighting',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 20,
    paths: ['M5 0 V5', circle(5, 10, 5), X_CROSS, 'M1 17.5 H9'],
    ports: inOnly(),
    tagPrefix: 'E',
    keywords: ['applique', 'wandlamp'],
  },
  {
    id: 'iec.light-linear',
    name: L('Réglette / tube', 'Linear luminaire', 'TL-armatuur / lichtlijn'),
    category: 'lighting',
    views: ['single-line', 'position-plan'],
    width: 20,
    height: 10,
    paths: ['M10 0 V5', 'M2 5 H18', 'M2 3 V7', 'M18 3 V7'],
    ports: inOnly(10),
    tagPrefix: 'E',
    keywords: ['TL', 'LED', 'néon', 'fluorescent', 'armatuur'],
  },
  {
    id: 'iec.light-recessed',
    name: L('Spot encastré', 'Recessed light', 'Inbouwspot'),
    category: 'lighting',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 15,
    paths: ['M5 0 V5', rect(0.5, 5.5, 9, 9), circle(5, 10, 3.5), 'M2.5 7.5 L7.5 12.5 M7.5 7.5 L2.5 12.5'],
    ports: inOnly(),
    tagPrefix: 'E',
    fields: [F.count()],
    label: ['{count}×'],
    keywords: ['spot', 'inbouwspot', 'downlight'],
  },
  {
    id: 'iec.light-emergency',
    name: L('Éclairage de secours', 'Emergency light', 'Noodverlichting'),
    category: 'lighting',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 15,
    paths: ['M5 0 V5', rect(0, 5, 10, 10), 'M0 5 L10 15 M10 5 L0 15'],
    fills: [circle(5, 10, 1.2)],
    ports: inOnly(),
    tagPrefix: 'E',
    keywords: ['secours', 'BAES', 'noodverlichting'],
  },

  // ===== Commands (installation switches, plan & single-line) =====
  {
    id: 'iec.switch',
    name: L('Interrupteur simple', 'One-way switch', 'Enkelpolige schakelaar'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [swCircle, 'M6.06 3.94 L9 1', tick(9, 1, 0.9, 0.9)],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['interrupteur', 'schakelaar', 'enkelpolig'],
  },
  {
    id: 'iec.switch-double',
    name: L('Interrupteur double', 'Double switch', 'Dubbele schakelaar'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [swCircle, 'M6.06 3.94 L9 1', tick(9, 1, 0.9, 0.9), 'M6.5 5 L9.5 3', tick(9.5, 3, 0.55, 0.95)],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['double allumage', 'dubbele schakelaar', 'serie'],
  },
  {
    id: 'iec.switch-twoway',
    name: L('Interrupteur va-et-vient', 'Two-way switch', 'Wisselschakelaar'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [swCircle, 'M6.06 3.94 L9 1', tick(9, 1, 0.9, 0.9), 'M3.94 6.06 L1 9', tick(1, 9, -0.9, -0.9)],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['va-et-vient', 'wissel', 'hotel', 'wisselschakelaar'],
  },
  {
    id: 'iec.switch-intermediate',
    name: L('Interrupteur permutateur', 'Intermediate switch', 'Kruisschakelaar'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [
      swCircle,
      'M1 9 L9 1',
      tick(9, 1, 0.9, 0.9),
      tick(1, 9, -0.9, -0.9),
      'M1 1 L9 9',
      tick(9, 9, 0.9, -0.9),
      tick(1, 1, -0.9, 0.9),
    ],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['permutateur', 'croisement', 'kruisschakelaar'],
  },
  {
    id: 'iec.pushbutton',
    name: L('Bouton-poussoir', 'Push-button', 'Drukknop'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [circle(5, 5, 2.5), circle(5, 5, 1)],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['poussoir', 'drukknop', 'télérupteur'],
  },
  {
    id: 'iec.dimmer',
    name: L('Variateur', 'Dimmer', 'Dimmer'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [swCircle, 'M6.06 3.94 L9 1', tick(9, 1, 0.9, 0.9), 'M2.5 8.5 L7.5 1.5'],
    fills: ['M7.5 1.5 L6.4 2.4 L7.4 3.1 Z'],
    ports: swPort,
    tagPrefix: 'S',
    keywords: ['variateur', 'dimmer', 'dimschakelaar'],
  },
  {
    id: 'iec.motion-detector',
    name: L('Détecteur de mouvement', 'Motion detector', 'Bewegingsmelder'),
    category: 'command',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: [rect(1, 3, 8, 6), 'M2.5 7.5 A2.5 2.5 0 0 1 7.5 7.5', 'M3.75 7.5 A1.25 1.25 0 0 1 6.25 7.5'],
    ports: [{ id: 'in', x: 5, y: 10, dir: 'down' }],
    tagPrefix: 'B',
    keywords: ['détecteur', 'PIR', 'bewegingsmelder', 'présence'],
  },
  {
    id: 'iec.thermostat',
    name: L('Thermostat', 'Thermostat', 'Thermostaat'),
    category: 'command',
    views: ['single-line', 'position-plan', 'control'],
    width: 10,
    height: 10,
    paths: [rect(1, 2, 8, 6), 'M5 8 V10'],
    texts: [{ text: '°C', x: 5, y: 5.9, size: 2.6 }],
    ports: [{ id: 'in', x: 5, y: 10, dir: 'down' }],
    tagPrefix: 'B',
    keywords: ['thermostaat', 'température'],
  },

  // ===== Control circuits =====
  {
    id: 'iec.contact-no',
    name: L('Contact à fermeture (NO)', 'Normally open contact', 'Maakcontact (NO)'),
    category: 'control',
    views: ['multi-line', 'control'],
    width: 10,
    height: 15,
    paths: noContact,
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['NO', 'contact', 'maakcontact', '13-14'],
  },
  {
    id: 'iec.contact-nc',
    name: L('Contact à ouverture (NF)', 'Normally closed contact', 'Verbreekcontact (NC)'),
    category: 'control',
    views: ['multi-line', 'control'],
    width: 10,
    height: 15,
    paths: ncContact,
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['NC', 'NF', 'contact', 'verbreekcontact', '21-22'],
  },
  {
    id: 'iec.contact-changeover',
    name: L('Contact inverseur', 'Changeover contact', 'Wisselcontact'),
    category: 'control',
    views: ['multi-line', 'control'],
    width: 10,
    height: 15,
    paths: ['M2.5 0 V5', 'M7.5 0 V5 H6', 'M5 10 L6.6 4.5', 'M5 10 V15'],
    ports: [
      { id: 'no', x: 2.5, y: 0, dir: 'up' },
      { id: 'nc', x: 7.5, y: 0, dir: 'up' },
      { id: 'c', x: 5, y: 15, dir: 'down' },
    ],
    tagPrefix: 'K',
    keywords: ['inverseur', 'wisselcontact', 'CO'],
  },
  {
    id: 'iec.contact-no-delay',
    name: L('Contact temporisé à la fermeture', 'On-delay NO contact', 'Vertraagd maakcontact'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...noContact, 'M3.25 7 H0.25', 'M0.25 5.5 A1.5 1.5 0 0 0 0.25 8.5'],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['temporisé', 'timer', 'vertraagd'],
  },
  {
    id: 'iec.contact-nc-delay',
    name: L("Contact temporisé à l'ouverture", 'Off-delay NC contact', 'Vertraagd verbreekcontact'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...ncContact, 'M6.75 7.5 H9.75', 'M9.75 6 A1.5 1.5 0 0 1 9.75 9'],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['temporisé', 'timer', 'vertraagd'],
  },
  {
    id: 'iec.pushbutton-no',
    name: L('Bouton-poussoir (NO)', 'Push-button (NO)', 'Drukknop (NO)'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...noContact, actuator(), 'M-2.5 5.5 V8.5 M-2.5 5.5 H-1.5 M-2.5 8.5 H-1.5'],
    ports: inOut(15),
    tagPrefix: 'S',
    keywords: ['marche', 'start', 'drukknop'],
  },
  {
    id: 'iec.pushbutton-nc',
    name: L('Bouton-poussoir (NF)', 'Push-button (NC)', 'Drukknop (NC)'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...ncContact, 'M6.75 7.25 H11.5', 'M11.5 5.75 V8.75 M11.5 5.75 H10.5 M11.5 8.75 H10.5'],
    ports: inOut(15),
    tagPrefix: 'S',
    keywords: ['arrêt', 'stop', 'drukknop'],
  },
  {
    id: 'iec.emergency-stop',
    name: L("Arrêt d'urgence", 'Emergency stop', 'Noodstop'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...ncContact, 'M6.75 7.25 H11', 'M11 4.75 A2.5 2.5 0 0 1 11 9.75 Z'],
    ports: inOut(15),
    tagPrefix: 'S',
    keywords: ['urgence', 'noodstop', 'e-stop', 'coup de poing'],
  },
  {
    id: 'iec.selector',
    name: L('Commutateur rotatif', 'Selector switch', 'Keuzeschakelaar'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...noContact, actuator(), 'M-2.5 5.5 L-1.5 5.5 L-2.5 8.5 L-1.5 8.5'],
    ports: inOut(15),
    tagPrefix: 'S',
    keywords: ['sélecteur', 'commutateur', 'keuzeschakelaar'],
  },
  {
    id: 'iec.limit-switch',
    name: L('Fin de course (NO)', 'Limit switch (NO)', 'Eindschakelaar (NO)'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...noContact, 'M3.25 7 L1.75 8.25 L3.6 8.5'],
    ports: inOut(15),
    tagPrefix: 'B',
    keywords: ['fin de course', 'eindeloop', 'limit'],
  },
  {
    id: 'iec.overload-contact',
    name: L(
      'Contact du relais thermique (NF)',
      'Overload relay contact (NC)',
      'Contact thermisch relais (NC)',
    ),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: [...ncContact, 'M6.75 7.25 H9', rect(9, 5.5, 3, 3.5), 'M9 7.25 H10 V6.25 H11 V8.25 H12'],
    ports: inOut(15),
    tagPrefix: 'F',
    keywords: ['95-96', 'thermique', 'thermisch'],
  },
  {
    id: 'iec.coil',
    name: L('Bobine (contacteur, relais)', 'Coil (contactor, relay)', 'Spoel (contactor, relais)'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V4', rect(1, 4, 8, 7), 'M5 11 V15'],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['A1-A2', 'bobine', 'spoel', 'relais'],
  },
  {
    id: 'iec.coil-on-delay',
    name: L('Bobine temporisée au travail', 'On-delay timer coil', 'Spoel met inschakelvertraging'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V4', rect(1, 4, 8, 7), 'M5 11 V15', 'M1 4 L4 11 M4 4 L1 11', 'M4 4 V11'],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['temporisateur', 'timer', 'tijdrelais'],
  },
  {
    id: 'iec.coil-off-delay',
    name: L('Bobine temporisée au repos', 'Off-delay timer coil', 'Spoel met uitschakelvertraging'),
    category: 'control',
    views: ['control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V4', rect(1, 4, 8, 7), 'M5 11 V15', 'M4 4 V11'],
    fills: [rect(1, 4, 3, 7)],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['temporisateur', 'timer', 'tijdrelais'],
  },
  {
    id: 'iec.coil-impulse',
    name: L('Télérupteur (bobine)', 'Impulse relay (coil)', 'Teleruptor (spoel)'),
    category: 'control',
    views: ['single-line', 'control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V4', rect(1, 4, 8, 7), 'M5 11 V15', 'M4 4 V11', 'M2.5 5.5 V9.5'],
    ports: inOut(15),
    tagPrefix: 'K',
    keywords: ['télérupteur', 'teleruptor', 'impulsrelais'],
  },
  {
    id: 'iec.motor',
    name: L('Moteur', 'Motor', 'Motor'),
    category: 'motor',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V5', circle(5, 10, 5)],
    texts: [{ text: 'M', x: 5, y: 11.5, size: 4.5 }],
    ports: inOnly(),
    tagPrefix: 'M',
    fields: [F.powerKw()],
    label: ['{powerKw} kW'],
    keywords: ['moteur', 'motor', 'pompe', 'volet'],
  },
  {
    id: 'iec.motor-3ph',
    name: L('Moteur triphasé', 'Three-phase motor', 'Driefasige motor'),
    category: 'motor',
    views: ['multi-line', 'control'],
    width: 15,
    height: 15,
    paths: ['M2.5 0 V3 L4.4 6.3', 'M7.5 0 V4.5', 'M12.5 0 V3 L10.6 6.3', circle(7.5, 10, 5.5)],
    texts: [
      { text: 'M', x: 7.5, y: 10.6, size: 3.8 },
      { text: '3~', x: 7.5, y: 13.6, size: 2 },
    ],
    ports: [
      { id: 'u', x: 2.5, y: 0, dir: 'up' },
      { id: 'v', x: 7.5, y: 0, dir: 'up' },
      { id: 'w', x: 12.5, y: 0, dir: 'up' },
    ],
    tagPrefix: 'M',
    fields: [F.powerKw()],
    label: ['{powerKw} kW'],
    keywords: ['moteur', 'driefasige motor'],
  },

  // ===== Signalling =====
  {
    id: 'iec.signal-lamp',
    name: L('Voyant', 'Indicator lamp', 'Signaallamp'),
    category: 'signalling',
    views: ['multi-line', 'control'],
    width: 10,
    height: 20,
    paths: ['M5 0 V5', circle(5, 10, 5), X_CROSS, 'M5 15 V20'],
    ports: inOut(20),
    tagPrefix: 'P',
    keywords: ['voyant', 'lampe témoin', 'signaallamp'],
  },
  {
    id: 'iec.buzzer',
    name: L('Ronfleur', 'Buzzer', 'Zoemer'),
    category: 'signalling',
    views: ['control'],
    width: 10,
    height: 15,
    paths: ['M5 0 V5', 'M2 10 A3 3 0 0 1 8 10 Z', 'M5 10 V15'],
    ports: inOut(15),
    tagPrefix: 'P',
    keywords: ['ronfleur', 'zoemer'],
  },
  {
    id: 'iec.doorbell',
    name: L('Sonnerie', 'Doorbell', 'Bel'),
    category: 'signalling',
    views: ['single-line', 'position-plan', 'control'],
    width: 10,
    height: 10,
    paths: ['M5 0 V5.5', 'M1.5 9 A3.5 3.5 0 0 1 8.5 9', 'M1.5 9 H8.5'],
    ports: inOnly(),
    tagPrefix: 'P',
    keywords: ['sonnette', 'bel', 'carillon', 'deurbel'],
  },

  // ===== Connection =====
  {
    id: 'iec.terminal',
    name: L('Borne', 'Terminal', 'Klem'),
    category: 'connection',
    views: ['multi-line', 'control'],
    width: 5,
    height: 5,
    paths: [circle(2.5, 2.5, 1.25)],
    ports: [
      { id: 'a', x: 2.5, y: 0, dir: 'up' },
      { id: 'b', x: 2.5, y: 5, dir: 'down' },
    ],
    tagPrefix: 'X',
    tagFormat: '{tag}:{number}',
    fields: [{ key: 'number', label: L('Numéro de borne', 'Terminal number', 'Klemnummer'), type: 'text' }],
    keywords: ['bornier', 'klem'],
  },
  {
    // Off-page connector: the conductor continues on another sheet, at the connector with the
    // same link name. Drawn as an arrow pointing away from the conductor.
    id: 'iec.offpage',
    name: L(
      'Renvoi (suite sur une autre feuille)',
      'Off-page connector',
      'Verwijzing (vervolg op een ander blad)',
    ),
    category: 'connection',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 5,
    paths: ['M0 2.5L2.5 2.5M2.5 0.5L7.5 0.5L10 2.5L7.5 4.5L2.5 4.5Z'],
    ports: [{ id: 'a', x: 0, y: 2.5, dir: 'left' }],
    tagPrefix: '',
    fields: [{ key: 'link', label: L('Renvoi', 'Link', 'Verwijzing'), type: 'text' }],
    label: ['{link}'],
    keywords: ['renvoi', 'suite', 'continuation', 'verwijzing', 'off-page'],
  },
  {
    id: 'iec.junction',
    name: L('Boîte de dérivation', 'Junction box', 'Aftakdoos'),
    category: 'connection',
    views: ['single-line', 'position-plan'],
    width: 5,
    height: 5,
    paths: [],
    fills: [circle(2.5, 2.5, 2.5)],
    ports: [{ id: 'in', x: 2.5, y: 0, dir: 'up' }],
    tagPrefix: 'J',
    keywords: ['dérivation', 'aftakdoos'],
  },
  {
    id: 'iec.board',
    name: L('Tableau de distribution', 'Distribution board', 'Verdeelbord'),
    category: 'connection',
    views: ['single-line', 'position-plan'],
    width: 15,
    height: 10,
    paths: [rect(0, 2.5, 15, 7.5)],
    fills: ['M0 10 L15 2.5 V10 Z'],
    ports: [{ id: 'in', x: 7.5, y: 0, dir: 'up' }],
    tagPrefix: 'A',
    keywords: ['tableau', 'coffret', 'verdeelbord', 'zekeringkast', 'divisionnaire'],
  },

  // ===== Earth =====
  {
    id: 'iec.earth',
    name: L('Terre', 'Earth', 'Aarde'),
    category: 'earth',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 10,
    paths: ['M5 0 V5', 'M1 5 H9', 'M2.5 7 H7.5', 'M4 9 H6'],
    ports: inOnly(),
    tagPrefix: 'PE',
    keywords: ['terre', 'aarde', 'ground'],
  },
  {
    id: 'iec.protective-earth',
    name: L('Terre de protection', 'Protective earth', 'Beschermingsaarde'),
    category: 'earth',
    views: ['single-line', 'multi-line', 'control'],
    width: 10,
    height: 15,
    paths: [
      'M5 0 V4.5',
      circle(5, 9, 4.5),
      'M5 6.5 V7.75',
      'M2.5 7.75 H7.5',
      'M3.5 9.5 H6.5',
      'M4.4 11.25 H5.6',
    ],
    ports: inOnly(),
    tagPrefix: 'PE',
    keywords: ['PE', 'beschermingsaarde'],
  },
  {
    id: 'iec.earth-electrode',
    name: L('Prise de terre', 'Earth electrode', 'Aardverbinding'),
    category: 'earth',
    views: ['single-line', 'position-plan'],
    width: 10,
    height: 15,
    paths: ['M5 0 V14', 'M1 5 H9', 'M2.5 7 H7.5', 'M4 9 H6'],
    ports: inOnly(),
    tagPrefix: 'PE',
    fields: [
      {
        key: 'resistanceOhm',
        label: L('Résistance de dispersion', 'Earth resistance', 'Spreidingsweerstand'),
        type: 'number',
        unit: 'Ω',
        min: 0,
      },
    ],
    label: ['{resistanceOhm} Ohm'],
    keywords: ['boucle de terre', 'piquet', 'aardlus', 'aardingsonderzoek'],
  },
  {
    id: 'iec.earth-bar',
    name: L('Borne principale de terre', 'Main earthing terminal', 'Hoofdaardingsklem'),
    category: 'earth',
    views: ['single-line', 'multi-line'],
    width: 15,
    height: 10,
    paths: [rect(1, 2.5, 13, 5), 'M7.5 0 V2.5', 'M7.5 7.5 V10'],
    ports: inOut(10, 7.5),
    tagPrefix: 'PE',
    keywords: ['barrette', 'aardingsklem', 'sectionneur de terre', 'borne de terre'],
  },
  {
    id: 'iec.equipotential',
    name: L('Liaison équipotentielle', 'Equipotential bonding', 'Equipotentiaalverbinding'),
    category: 'earth',
    views: ['single-line', 'multi-line', 'position-plan'],
    width: 10,
    height: 10,
    paths: ['M5 0 V3', 'M1.5 3 H8.5 L5 9 Z'],
    ports: inOnly(),
    tagPrefix: 'PE',
    keywords: ['équipotentielle', 'equipotentiaal', 'bonding'],
  },

  // ===== Position plan =====
  {
    id: 'board.module',
    name: L(
      'Appareil modulaire (vue de face)',
      'Modular device (front view)',
      'Modulair toestel (vooraanzicht)',
    ),
    category: 'annotation',
    views: [],
    width: 20,
    height: 45,
    paths: [],
    ports: [],
    tagPrefix: '',
    box: true,
    keywords: ['rail DIN', 'module', 'tableau'],
  },
  {
    id: 'plan.room',
    name: L('Pièce', 'Room', 'Ruimte'),
    category: 'annotation',
    views: ['position-plan'],
    width: 40,
    height: 30,
    paths: [],
    ports: [],
    tagPrefix: '',
    room: true,
    fields: [
      { key: 'name', label: L('Nom', 'Name', 'Naam'), type: 'text', default: 'Pièce' },
      {
        key: 'roomType',
        label: L('Type de pièce', 'Room type', 'Soort ruimte'),
        type: 'select',
        options: ROOM_TYPES.map((r) => ({ value: r.value, label: r.label })),
        default: 'other',
      },
      {
        key: 'w',
        label: L('Largeur', 'Width', 'Breedte'),
        type: 'number',
        unit: 'mm',
        default: 40,
        min: 5,
        step: 2.5,
      },
      {
        key: 'h',
        label: L('Hauteur', 'Height', 'Hoogte'),
        type: 'number',
        unit: 'mm',
        default: 30,
        min: 5,
        step: 2.5,
      },
    ],
    keywords: ['pièce', 'local', 'room', 'kamer', 'ruimte'],
  },

  // ===== Annotation =====
  {
    id: 'annot.text',
    name: L('Texte', 'Text', 'Tekst'),
    category: 'annotation',
    views: ['single-line', 'multi-line', 'control', 'position-plan'],
    width: 20,
    height: 5,
    paths: [],
    ports: [],
    tagPrefix: '',
    freeText: true,
    fields: [
      { key: 'text', label: L('Texte', 'Text', 'Tekst'), type: 'text', default: 'Texte' },
      {
        key: 'size',
        label: L('Taille', 'Size', 'Grootte'),
        type: 'number',
        unit: 'mm',
        default: 3.5,
        min: 1.5,
        step: 0.5,
      },
    ],
    keywords: ['texte', 'annotation', 'note', 'label', 'tekst'],
  },
];

// ===== Multi-pole devices for multi-line and control diagrams =====

function rcdMultipole(n: number): SymbolDef {
  const s = multipole(
    {
      id: `iec.rcd-${n}p`,
      name: L(`Interrupteur différentiel ${n}P`, `RCD ${n}P`, `Differentieelschakelaar ${n}P`),
      category: 'protection',
      views: ['multi-line'],
      tagPrefix: 'Q',
      fields: [F.ratingA(40), F.sensitivityMa(), F.rcdType()],
      label: ['{ratingA} A', '{sensitivityMa} mA', 'type {rcdType}'],
      keywords: ['RCD', 'RCCB', 'différentiel'],
    },
    n,
    switchPole,
    20,
    null,
  );
  const boxX = n * 10;
  const box = rcdBox(boxX);
  return {
    ...s,
    width: n * 10 + 5,
    paths: [...s.paths, ...box.paths],
    texts: box.texts,
    dashed: [`M3 10.75 H${boxX}`],
  };
}

const MP: SymbolDef[] = [
  ...[2, 3, 4].map((n) =>
    multipole(
      {
        id: `iec.breaker-${n}p`,
        name: L(`Disjoncteur ${n}P`, `Circuit breaker ${n}P`, `Automaat ${n}P`),
        category: 'protection',
        views: ['multi-line', 'control'],
        tagPrefix: 'Q',
        fields: [F.ratingA(), F.curve(), F.breakingKa()],
        label: ['{curve}{ratingA} A'],
        keywords: ['MCB', 'disjoncteur'],
      },
      n,
      breakerPole,
      20,
      10.75,
    ),
  ),
  ...[2, 4].map(rcdMultipole),
  ...[2, 3, 4].map((n) =>
    multipole(
      {
        id: `iec.switch-disconnector-${n}p`,
        name: L(`Interrupteur-sectionneur ${n}P`, `Switch-disconnector ${n}P`, `Lastscheider ${n}P`),
        category: 'protection',
        views: ['multi-line', 'control'],
        tagPrefix: 'Q',
        fields: [F.ratingA(40)],
        label: ['{ratingA} A'],
        keywords: ['sectionneur', 'hoofdschakelaar'],
      },
      n,
      disconnectorPole,
      20,
      10.75,
    ),
  ),
  multipole(
    {
      id: 'iec.fuse-3p',
      name: L('Fusibles 3P', 'Fuses 3P', 'Smeltveiligheden 3P'),
      category: 'protection',
      views: ['multi-line', 'control'],
      tagPrefix: 'F',
      fields: [F.ratingA()],
      label: ['{ratingA} A'],
      keywords: ['fusible', 'zekering'],
    },
    3,
    fusePole,
    20,
    null,
  ),
  multipole(
    {
      id: 'iec.contactor-3p',
      name: L(
        'Contacteur 3P (puissance)',
        'Contactor 3P (main contacts)',
        'Contactor 3P (vermogenscontacten)',
      ),
      category: 'distribution',
      views: ['multi-line', 'control'],
      tagPrefix: 'K',
      fields: [F.ratingA(25)],
      label: ['{ratingA} A'],
      keywords: ['contacteur', 'magneetschakelaar'],
    },
    3,
    contactorPole,
    15,
    9,
  ),
  multipole(
    {
      id: 'iec.overload-3p',
      name: L('Relais thermique 3P', 'Thermal overload relay 3P', 'Thermisch relais 3P'),
      category: 'protection',
      views: ['multi-line', 'control'],
      tagPrefix: 'F',
      fields: [F.setting()],
      label: ['{settingA} A'],
      keywords: ['thermique', 'thermisch relais'],
    },
    3,
    (c) => [...overloadPole(c), rect(c - 3, 3, 6, 9)],
    15,
    null,
  ),
];

export const SYMBOLS: SymbolDef[] = [...S, ...MP];

export const SYMBOLS_BY_ID: ReadonlyMap<string, SymbolDef> = new Map(SYMBOLS.map((s) => [s.id, s]));

/**
 * Symbols drawn by the user live in the project (`project.symbols`, ids "user.…"). The project
 * being worked on registers them here, so every package resolves them like library symbols.
 */
const USER_SYMBOLS = new Map<string, SymbolDef>();
let userSource: object | undefined;

export function registerProjectSymbols(symbols: Readonly<Record<string, unknown>> | undefined): void {
  if (!symbols || symbols === userSource) return;
  userSource = symbols;
  USER_SYMBOLS.clear();
  for (const s of Object.values(symbols) as SymbolDef[])
    // Symbols drawn before Dutch existed: their French name stands in.
    USER_SYMBOLS.set(s.id, s.name.nl ? s : { ...s, name: { ...s.name, nl: s.name.fr } });
}

export const userSymbols = (): SymbolDef[] => [...USER_SYMBOLS.values()];

export function getSymbol(id: string): SymbolDef | undefined {
  return SYMBOLS_BY_ID.get(id) ?? USER_SYMBOLS.get(id);
}

/** Symbols offered for a sheet kind, in library order, then the user's symbols. */
export function symbolsForView(view: string): SymbolDef[] {
  return [...SYMBOLS, ...USER_SYMBOLS.values()].filter((s) => (s.views as string[]).includes(view));
}

const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** Case- and accent-insensitive search over names (both languages), id and keywords. */
export function searchSymbols(query: string, pool: readonly SymbolDef[] = SYMBOLS): SymbolDef[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [...pool];
  return pool.filter((s) => {
    const hay = norm([s.name.fr, s.name.en, s.name.nl, s.id, ...(s.keywords ?? [])].join(' '));
    return words.every((w) => hay.includes(w));
  });
}
